import { makeApi, Zodios, type ZodiosOptions } from "@zodios/core";
import { z } from "zod";

const IssuersEnum = z.enum([
  "AMERICAN_EXPRESS",
  "BANK_OF_AMERICA",
  "BARCLAYS",
  "BREX",
  "CHASE",
  "CAPITAL_ONE",
  "CITI",
  "COMENITY",
  "DISCOVER",
  "FIRST",
  "FNBO",
  "PENFED",
  "PNC",
  "SYNCHRONY",
  "TD",
  "US_BANK",
  "WEB_BANK",
  "WELLS_FARGO",
]);
const NetworksEnum = z.enum([
  "VISA",
  "MASTERCARD",
  "AMERICAN_EXPRESS",
  "DISCOVER",
]);
const CurrenciesEnum = z.enum([
  "BEST_WESTERN",
  "BREEZE",
  "HILTON",
  "HYATT",
  "IHG",
  "MARRIOTT",
  "RADISSON",
  "WYNDHAM",
  "CHOICE",
  "AEROPLAN",
  "ALASKA",
  "AMERICAN",
  "ANA",
  "AVIANCA",
  "AVIOS",
  "CATHAY_PACIFIC",
  "DELTA",
  "EMIRATES",
  "FRONTIER",
  "FLYING_BLUE",
  "HAWAIIAN",
  "JETBLUE",
  "KOREAN",
  "LATAM",
  "LUFTHANSA",
  "SOUTHWEST",
  "SPIRIT",
  "UNITED",
  "VIRGIN",
  "AMERICAN_EXPRESS",
  "BANK_OF_AMERICA",
  "BARCLAYS",
  "BILT",
  "BREX",
  "CHASE",
  "CITI",
  "CAPITAL_ONE",
  "DISCOVER",
  "US_BANK",
  "WELLS_FARGO",
  "CARNIVAL",
  "AMTRAK",
  "PENFED",
  "EXPEDIA",
  "USD",
]);
const Credit = z
  .object({
    description: z.string().min(1),
    value: z.number(),
    weight: z.number().gte(0).lte(1),
    currency: CurrenciesEnum.optional(),
  })
  .passthrough();
const OfferAmount = z
  .object({ amount: z.number(), currency: CurrenciesEnum.optional() })
  .passthrough();
const Expiration = z.string();
const Offer = z
  .object({
    spend: z.number().gte(0),
    amount: z.array(OfferAmount),
    days: z.number().gte(30),
    expiration: Expiration.optional(),
    isPublic: z.boolean().optional(),
    credits: z.array(Credit).default([]),
    details: z.string().optional(),
    url: z.string().optional(),
    referralUrl: z.string().optional(),
  })
  .passthrough();
const CreditCard = z
  .object({
    cardId: z.string().min(1),
    name: z.string().min(1),
    issuer: IssuersEnum,
    network: NetworksEnum,
    currency: CurrenciesEnum,
    countsTowards524: z.boolean().optional(),
    details: z.string().optional(),
    isBusiness: z.boolean(),
    annualFee: z.number(),
    isAnnualFeeWaived: z.boolean(),
    universalCashbackPercent: z.number().gte(1).lte(100),
    url: z.string(),
    imageUrl: z.string(),
    credits: z.array(Credit).default([]),
    offers: z.array(Offer).default([]),
    historicalOffers: z.array(Offer).default([]),
    discontinued: z.boolean().default(false),
  })
  .passthrough();

export const schemas = {
  IssuersEnum,
  NetworksEnum,
  CurrenciesEnum,
  Credit,
  OfferAmount,
  Expiration,
  Offer,
  CreditCard,
};

const endpoints = makeApi([
  {
    method: "get",
    path: "/data.csv",
    alias: "getCsvData",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "get",
    path: "/data.json",
    alias: "getJsonData",
    requestFormat: "json",
    response: z.array(CreditCard),
  },
  {
    method: "get",
    path: "/data.yaml",
    alias: "getYamlData",
    requestFormat: "json",
    response: z.void(),
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
